import { useState, useEffect, useMemo } from 'react';

/**
 * Device breakpoints matching hive-os design system
 */
export const BREAKPOINTS = {
  mobile: 768,     // < 768px
  tablet: 1280,    // 768px - 1279px
  desktop: 1280,   // >= 1280px
  largeDesktop: 1440, // >= 1440px
} as const;

export interface DeviceState {
  /** Current window width in pixels */
  windowWidth: number;
  /** True when screen width < 768px */
  isMobile: boolean;
  /** True when screen width is 768px - 1279px */
  isTablet: boolean;
  /** True when screen width >= 1280px */
  isDesktop: boolean;
  /** True when screen width >= 1440px */
  isLargeDesktop: boolean;
}

/**
 * Hook to detect device size and provide responsive state.
 *
 * Breakpoints match hive-os design:
 * - Mobile: < 768px (bottom nav, full-screen chat)
 * - Tablet: 768px - 1279px (collapsed sidebar)
 * - Desktop: >= 1280px (sidebar based on chat state)
 * - Large Desktop: >= 1440px (expanded sidebar even with chat)
 *
 * @example
 * ```tsx
 * const { isMobile, isTablet, isLargeDesktop } = useDeviceAwareness();
 *
 * // Auto-collapse sidebar on tablet or when chat is open on smaller screens
 * useEffect(() => {
 *   if (isTablet || (isChatOpen && !isLargeDesktop)) {
 *     setIsSidebarCollapsed(true);
 *   } else if (isLargeDesktop) {
 *     setIsSidebarCollapsed(false);
 *   }
 * }, [isChatOpen, isTablet, isLargeDesktop]);
 * ```
 */
export function useDeviceAwareness(): DeviceState {
  const [windowWidth, setWindowWidth] = useState(() => {
    if (typeof window === 'undefined') return BREAKPOINTS.desktop;
    return window.innerWidth;
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    // Set initial value
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return useMemo(() => ({
    windowWidth,
    isMobile: windowWidth < BREAKPOINTS.mobile,
    isTablet: windowWidth >= BREAKPOINTS.mobile && windowWidth < BREAKPOINTS.tablet,
    isDesktop: windowWidth >= BREAKPOINTS.desktop,
    isLargeDesktop: windowWidth >= BREAKPOINTS.largeDesktop,
  }), [windowWidth]);
}

/**
 * Hook to determine if sidebar should be auto-collapsed.
 *
 * Sidebar collapse rules from hive-os:
 * 1. Always collapse on tablet (768-1279px)
 * 2. Collapse when chat OR detail panel is open and screen < 1440px
 * 3. Expand on large desktop (>= 1440px) only when neither panel is open
 */
export function useSidebarAutoCollapse(
  isChatOpen: boolean,
  isDetailPanelOpen: boolean = false,
): boolean {
  const { isTablet, windowWidth, isLargeDesktop } = useDeviceAwareness();

  return useMemo(() => {
    if (isTablet) return true;
    const anyPanelOpen = isChatOpen || isDetailPanelOpen;
    if (anyPanelOpen && windowWidth < BREAKPOINTS.largeDesktop) return true;
    if (isLargeDesktop) return false;
    return false;
  }, [isTablet, isChatOpen, isDetailPanelOpen, windowWidth, isLargeDesktop]);
}
